import React from 'react';
import PropTypes from 'prop-types';
import { defaultTemplate } from 'core/hoc';
import { ApplicationEmptyState, BaasicButton, BaasicDropdown, ScrollToTopButton, BaasicDraggableModal, BaasicModal } from 'core/components';
import { PreviewLayout } from 'core/layouts';
import { NikoFacilityOrderStatusBar, NikoFacilityOrderReasonForChangeModal } from "application/niko-facility-order/components";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { Button } from "@progress/kendo-react-buttons";
import moment from "moment";
import { observer } from "mobx-react";
import { NikoPatientInformationBar, NikoPatientInsuranceInfoNoteModal, NikoPatientStatusModal } from "application/niko-patient/components";
import { toDate } from "core/utils";

function NikoFacilityOrderInsuranceReviewPreviewTemplate({ nikoFacilityOrderInsuranceReviewPreviewViewStore }) {
  const {
    loaderStore,
    refreshKey,
    goToFacilityPreview,
    goToFacilityCalendar,
    tabItems,
    selectedStatusAbrv,
    setStatus,
    startInsuranceReviewStatus,
    disabledByStatus,
    disabledBySendOrder,
    fetchingData,
    disabled,
    insuranceReviewStatus,
    isInitialIntakeCompleted,
    nikoPatientDetails,
    isReviewed,
    setInsuranceReviewStatusComplete,
    restartInsuranceReview,
    nikoPatientInsuranceInfoNoteModal,
    nikoPatientStatusModal,
    nikoFacilityOrderReasonForChangeModal,
    orderHistory,
    facilityOrder,
    isHistoryVisible,
    isInventoryStepAllowed,
    disabledByCompletion,
    usIntakeStatus,
    isSkipped,
  } = nikoFacilityOrderInsuranceReviewPreviewViewStore;

  return (
    <PreviewLayout className="container container--new-flow" store={nikoFacilityOrderInsuranceReviewPreviewViewStore} emptyRenderer={<ApplicationEmptyState />} layoutFooterVisible={false} loading={loaderStore.loading}>
      <ReactTooltip positionStrategy="fixed" id="usIntakeTooltip" place="bottom" />
      {facilityOrder && facilityOrder.facility && <NikoFacilityOrderStatusBar key={refreshKey} title={`${facilityOrder.facility.name} - DOS ${toDate(facilityOrder.dateOfService)}`} pageViewStore={nikoFacilityOrderInsuranceReviewPreviewViewStore} isInventoryStepAllowed={isInventoryStepAllowed}>
        <div className="content__header__actions card--tertiary__data__item">
          {isHistoryVisible && <a className="btn btn--base btn--light u-mar--right--tny" target='_blank' data-tooltip-content="Facility Order History" data-tooltip-id="usIntakeTooltip" onClick={() => orderHistory()}><i className="u-icon u-icon--med-lrg u-icon--us-history"></i></a>}
          <a className="btn btn--base btn--light u-mar--right--tny" target='_blank' data-tooltip-content="Facility Preview" data-tooltip-id="usIntakeTooltip" onClick={() => goToFacilityPreview()}><i className="u-icon u-icon--sml u-icon--preview--dark"></i></a>
          <a className="btn btn--base btn--light u-mar--right--tny" target='_blank' data-tooltip-content="Ordering Calendar" data-tooltip-id="usIntakeTooltip" onClick={() => goToFacilityCalendar()}><i className="u-icon u-icon--sml u-icon--schedules"></i></a>
        </div>
      </NikoFacilityOrderStatusBar>}
      <div className="u-flex--space-between">
        <div className="tabs__wrapper">
          <div className="tabs tabs--fullwidth">
            {tabItems.map(item =>
              <BaasicButton key={item.abrv}
                className={"tabs__item " + (item.abrv == selectedStatusAbrv ? 'active' : '')}
                label={item.name}
                onClick={() => item.abrv != selectedStatusAbrv ? setStatus(item.abrv) : {}}
              />
            )}
          </div>
        </div>
        <div className="u-flex--default u-mar--left--auto">
          {disabledByStatus && isInitialIntakeCompleted && !isSkipped &&
            <BaasicButton className="btn btn--base btn--primary u-mar--right--sml" disabled={disabledBySendOrder || disabled} label="Start" onClick={() => startInsuranceReviewStatus()} />
          }
        </div>
      </div>
      {disabledByStatus && !isInitialIntakeCompleted && !disabledByCompletion &&
        <div className="u-mar--bottom--sml">
          Insurance Review cannot be started because Initial Intake is not yet completed.
        </div>
      }
      {usIntakeStatus && usIntakeStatus.startedOn && !(insuranceReviewStatus && insuranceReviewStatus.completedOn) &&
        <div className="u-mar--bottom--sml">
          {insuranceReviewStatus && insuranceReviewStatus.startedOn ? 'US Intake has started working on this facility. No further adjustments can be made from the Insurance Review flow.' : 'Insurance Review cannot be worked on because US Intake has already started working on this facility order.'}
        </div>
      }
      {fetchingData ?
        <div>Loading...</div>
        :
        <React.Fragment>
          {selectedStatusAbrv === "in-progress" && insuranceReviewStatus && insuranceReviewStatus.completedOn &&
            <div className='u-mar--bottom--tny'>
              <div className="u-mar--bottom--sml">Insurance Review has been completed on {moment(insuranceReviewStatus.completedOn).format("MM/DD/YYYY, hh:mm A")}</div>
            </div>
          }
          {(nikoPatientDetails && nikoPatientDetails.length > 0) ?
            nikoPatientDetails.map(nikoPatientDetail => {
              return (
                <NikoPatientTemplate
                  key={nikoPatientDetail.id}
                  nikoFacilityOrderInsuranceReviewPreviewViewStore={nikoFacilityOrderInsuranceReviewPreviewViewStore}
                  nikoPatientDetail={nikoPatientDetail}
                />
              );
            })
            :
            selectedStatusAbrv === "in-progress" &&
            <React.Fragment>
              {insuranceReviewStatus && insuranceReviewStatus.completedOn && !(usIntakeStatus && usIntakeStatus.startedOn) ?
                <div className="empty-state--facility-orders">
                  <div className="empty-state--facility-orders--table"></div>
                  <p className="empty-state--facility-orders--description">You have no orders in progress!</p>
                  <BaasicButton className="btn btn--base btn--primary" label="Restart Insurance Review" disabled={disabled || disabledBySendOrder} onClick={() => restartInsuranceReview()} />
                </div>
                :
                <React.Fragment>
                  {isReviewed &&
                    <div className="empty-state--facility-orders">
                      <div className="empty-state--facility-orders--table"></div>
                      <p className="empty-state--facility-orders--description">You have no orders in progress!</p>
                      <BaasicButton
                        className="btn btn--base btn--primary"
                        label="Insurance Review is Complete"
                        onClick={() => setInsuranceReviewStatusComplete()}
                      />
                    </div>
                  }
                  {isReviewed === false &&
                    <div className="u-mar--bottom--sml">
                      All In Progress templates have been reviewed. Please review the discharged templates to complete Insurance Review.
                    </div>
                  }
                </React.Fragment>
              }
            </React.Fragment>
          }
        </React.Fragment>
      }
      <BaasicDraggableModal modalParams={nikoPatientInsuranceInfoNoteModal} className="modal__content modal--med">
        <NikoPatientInsuranceInfoNoteModal />
      </BaasicDraggableModal>
      <BaasicModal modalParams={nikoPatientStatusModal} className="modal__content modal--med">
        <NikoPatientStatusModal />
      </BaasicModal>
      <BaasicModal modalParams={nikoFacilityOrderReasonForChangeModal} className="modal__content modal--lrg">
        <NikoFacilityOrderReasonForChangeModal />
      </BaasicModal>
      <ScrollToTopButton />
    </PreviewLayout>
  )
}

NikoFacilityOrderInsuranceReviewPreviewTemplate.propTypes = {
  nikoFacilityOrderInsuranceReviewPreviewViewStore: PropTypes.object.isRequired
};

const NikoPatientTemplate = observer(({ nikoFacilityOrderInsuranceReviewPreviewViewStore, nikoPatientDetail }) => {
  const {
    selectedStatusAbrv,
    disabledByStatus,
    disabledByCompletion,
    disabledBySendOrder,
    disabled,
    statusStore,
    inProgressStatusId,
    userId,
    openNikoPatientStatusModal,
    openReasonForChangeModal,
    reviewPatientTemplates,
    changeItemsVisibility,
    fetchInsuranceChangeHistory,
    insuranceChangeHistoryMap,
    showInsuranceChangeHistory
  } = nikoFacilityOrderInsuranceReviewPreviewViewStore;

  var ichVisible = showInsuranceChangeHistory === nikoPatientDetail.id;
  var ich = ichVisible ? (insuranceChangeHistoryMap[nikoPatientDetail.id] || {}) : {};

  var renderChangeHistory = function (fieldKey) {
    var entry = ich[fieldKey];
    if (!entry || !entry.userName) return null;
    var d = new Date(entry.eventDate + "Z");
    var fd = d.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }).replace(',', '');
    return <div className="card--info__wrap"><span className="card--info">{entry.userName}, {fd}</span></div>;
  };

  return (
    <React.Fragment>
      <ReactTooltip positionStrategy="fixed" id={"nikoPatientTooltip" + nikoPatientDetail.id} place="bottom" />

      <div className="card--tertiary u-mar--bottom--sml--alt">
        <div className={`card--tertiary__header is-niko ${selectedStatusAbrv === "discharge" ? "" : "card--tertiary__header--insurance"}`}>
          <div className="u-flex--default">
            <h4 className="card--tertiary__header__title">
              {nikoPatientDetail.nikoPatient.lastName && `${nikoPatientDetail.nikoPatient.lastName}`}
              {nikoPatientDetail.nikoPatient.lastName && nikoPatientDetail.nikoPatient.firstName && ', '}
              {nikoPatientDetail.nikoPatient.firstName && `${nikoPatientDetail.nikoPatient.firstName}`}
            </h4>
            <NikoPatientInformationBar nikoPatient={nikoPatientDetail.nikoPatient} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled} showVisibilityOption={false} disableEditMode={!nikoPatientDetail.isNew} />
          </div>
          {
            nikoPatientDetail.isNew && selectedStatusAbrv === "discharge" &&
            <div className="status status--sml status--success">
              <p>New Patient</p>
            </div>
          }
          <div className="u-flex--default u-align--h--right">
            <div className="w--200--px u-align--h--right u-mar--left--xsml">
              <BaasicDropdown store={statusStore} value={nikoPatientDetail.status} onChange={(e) => { openNikoPatientStatusModal(nikoPatientDetail, e.value.id) }} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled || (selectedStatusAbrv === "discharge" && nikoPatientDetail.insuranceStatusDischargedByUserId && nikoPatientDetail.insuranceStatusDischargedByUserId !== userId)} />
            </div>
          </div>
        </div>
        <React.Fragment>
          {selectedStatusAbrv !== "discharge" &&
            <div className='niko__status__wrapper u-mar--right--status-low'>
              {nikoPatientDetail.isNew ?
                <div className={` ${disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled ? "is-disabled" : ""}`}>
                  <div className="niko__status yellow no-bg">
                    <div className="niko__status__holder">
                      <Button className="niko__status__btn" icon="u-icon u-icon--xsml u-icon--persons--yellow" disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}>NEW PATIENT</Button>
                    </div>
                  </div>
                </div>
                :
                nikoPatientDetail.insuranceStatusId === inProgressStatusId ?
                  null
                  :
                  <div className={`${disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled ? "is-disabled" : ""}`}>
                    <div className={`niko__status yellow ${nikoPatientDetail.insuranceSelectedStatus === "success" ? "success" : "warning"}`}>
                      <div className="niko__status__holder">
                        <Button className={`niko__status__btn ${nikoPatientDetail.insuranceSelectedStatus === "success" ? "success" : "warning"}`}> {nikoPatientDetail.insuranceSelectedStatus === "success" ? "NO CHANGES" : "CHANGES"} </Button>
                      </div>
                    </div>
                  </div>
              }
            </div>
          }
          <div className="card--tertiary__subheader is-niko">
            <div className="card--tertiary__flex">
              <div className="status--sqare__wrapper u-mar--left--xsml">
                <BaasicButton
                  className="btn btn--base-plus btn--light btn--uppercase no-click"
                  label={nikoPatientDetail.eligibilityStatus ? nikoPatientDetail.eligibilityStatus.name : "Blank"}
                  disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}
                />
              </div>
              <div className="card--tertiary__data">
                <div>
                  <div className="card--tertiary__data__item">
                    <div>Primary:</div>
                    <div className="type--color--primary type--wgt--semibold">{nikoPatientDetail.primaryActive.insurance.current && nikoPatientDetail.primaryActive.insurance.current.name}</div>
                    {nikoPatientDetail.primaryActive.policyNo.current && <div>{nikoPatientDetail.primaryActive.policyNo.current}</div>}
                  </div>
                  {nikoPatientDetail.primaryInactive.insurance.current &&
                    <div className="card--tertiary__data__item">
                      <div>Inactive:</div>
                      <div className="type--color--primary type--wgt--semibold">{nikoPatientDetail.primaryInactive.insurance.current.name}</div>
                      <div>{nikoPatientDetail.primaryInactive.policyNo.current && nikoPatientDetail.primaryInactive.policyNo.current}</div>
                    </div>
                  }
                </div>
                <div>
                  <div className="card--tertiary__data__item">
                    <div>Secondary:</div>
                    <div className="type--color--primary type--wgt--semibold">{nikoPatientDetail.secondaryActive.insurance.current && nikoPatientDetail.secondaryActive.insurance.current.name}</div>
                    {nikoPatientDetail.secondaryActive.policyNo.current && <div>{nikoPatientDetail.secondaryActive.policyNo.current}</div>}
                  </div>
                  {nikoPatientDetail.secondaryInactive.insurance.current &&
                    <div className="card--tertiary__data__item">
                      <div>Inactive:</div>
                      <div className="type--color--primary type--wgt--semibold">{nikoPatientDetail.secondaryInactive.insurance.current.name}</div>
                      <div>{nikoPatientDetail.secondaryInactive.policyNo.current && nikoPatientDetail.secondaryInactive.policyNo.current}</div>
                    </div>
                  }
                </div>
              </div>
              <div className="u-flex u-flex--default u-align--h--right">
                <div className="btn--group" style={{ opacity: 1, visibility: 'visible' }}>
                  <Button className="btn no-hover" icon="u-icon u-icon--med-lrg u-icon--us-edit" onClick={() => { openReasonForChangeModal(nikoPatientDetail) }} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled} data-tooltip-content="Edit Insurances" data-tooltip-id={"nikoPatientTooltip" + nikoPatientDetail.id} />
                  <div className="btn--group__divider"></div>
                </div>
                  {nikoPatientDetail.patientTemplates.some(fot => fot.isPriorAuthNeeded) &&
                    <BaasicButton
                      type='button'
                      className="btn btn--base-plus btn--light is-grey no-click u-mar--right--tny"
                      label={`PAR ${nikoPatientDetail.patientTemplates.filter(fot => fot.isPriorAuthNeeded).length}/${nikoPatientDetail.patientTemplates.length}`}
                      disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}
                    />
                  }
                  {selectedStatusAbrv === "in-progress" &&
                    <div className="status--sqare__wrapper"
                         data-tooltip-content={!nikoPatientDetail.eligibilityStatusId ? "Eligibility Status must be selected" : ""}
                         data-tooltip-id={!nikoPatientDetail.eligibilityStatusId ? `nikoPatientTooltip${nikoPatientDetail.id}` : ""}>
                      <Button className="btn btn--icon btn--icon--lrg btn--primary u-mar--left--xsml" icon="u-icon u-icon--med u-icon--check-list"
                              onClick={() => { reviewPatientTemplates(nikoPatientDetail, null, true) }}
                              disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled || !nikoPatientDetail.eligibilityStatusId}
                              data-tooltip-content="Review" data-tooltip-id={"nikoPatientTooltip" + nikoPatientDetail.id} />
                    </div>
                  }
                  {selectedStatusAbrv === "intake-reviewed" &&
                    <Button className="btn btn--icon btn--icon--lrg btn--primary u-mar--left--xsml" icon="u-icon u-icon--med u-icon--unreview" onClick={() => { reviewPatientTemplates(nikoPatientDetail, null, false) }} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled} data-tooltip-content="Unreview" data-tooltip-id={"nikoPatientTooltip" + nikoPatientDetail.id} />
                  }
              </div>
            </div>
            <div>
              {nikoPatientDetail.insuranceInfoNote &&
                <div className="c-message has-input c-message--note u-mar--top--sml">
                  <i className="u-icon u-icon--sml u-icon--add-note" />
                  <input
                    className="input input--sml input--message u-flex--grow--1 type--sml"
                    value={nikoPatientDetail.insuranceInfoNote}
                    disabled
                    readOnly
                  />
                </div>
              }
              {(nikoPatientDetail.note || nikoPatientDetail.hasHistory) &&
                <div className={`u-mar--top--lrg c-message has-dom-badge ${!nikoPatientDetail.isNew ? 'c-message--orange' : 'c-message--note--primary c-message--note--primary--tab'}`}>
                  {nikoPatientDetail.note &&
                    <React.Fragment>
                    <div className='c-message__badge'>
                          <i className='u-icon u-icon--size--badge u-icon--info-fill--neg'
                            onMouseEnter={() => { fetchInsuranceChangeHistory(nikoPatientDetail); nikoFacilityOrderInsuranceReviewPreviewViewStore.showInsuranceChangeHistory = nikoPatientDetail.id; }}
                            onMouseLeave={() => { nikoFacilityOrderInsuranceReviewPreviewViewStore.showInsuranceChangeHistory = null; }}>
                          </i>
                      </div>
                    <div className={`has-input ${nikoPatientDetail.isNew || !nikoPatientDetail.hasHistory ? '' : 'c-message--has-border'} u-flex--default u-flex--gap--1 w--100`}>
                      Note: <p style={{ fontWeight: "normal" }}>{nikoPatientDetail.note}</p>
                      {renderChangeHistory("Note")}
                      {nikoPatientDetail.effectiveDate &&
                        <span className="u-align--h--right type--whitespace--nowrap">Effective Date: {toDate(nikoPatientDetail.effectiveDate)}</span>
                      }
                    </div>
                    </React.Fragment>
                  }
                  {!nikoPatientDetail.isNew &&
                    <React.Fragment>
                      <div className='c-message__badge'>
                          Changes
                          <i className='u-icon u-icon--size--badge u-icon--info-fill--neg'
                            onMouseEnter={() => { fetchInsuranceChangeHistory(nikoPatientDetail); nikoFacilityOrderInsuranceReviewPreviewViewStore.showInsuranceChangeHistory = nikoPatientDetail.id; }}
                            onMouseLeave={() => { nikoFacilityOrderInsuranceReviewPreviewViewStore.showInsuranceChangeHistory = null; }}>
                          </i>
                      </div>
                      {nikoPatientDetail.primaryActive.insuranceId.current != nikoPatientDetail.primaryActive.insuranceId.initial &&
                        <div className="u-flex--default u-flex--gap--2 c-message--content">
                          <p className="type--sml"><b>Primary Active Changed</b></p>
                          <label className="type--sml type--color--opaque"><b>{nikoPatientDetail.primaryActive.insurance.initial ? nikoPatientDetail.primaryActive.insurance.initial.Name : "Empty"}</b></label>
                          <p className="type--sml"><i className="u-icon u-icon--xxsml u-icon--arrow-text"></i></p>
                          <p className="type--sml"><b>{nikoPatientDetail.primaryActive.insurance.current ? nikoPatientDetail.primaryActive.insurance.current.name : "Empty"}</b></p>
                          {renderChangeHistory("PrimaryActiveInsuranceId")}
                        </div>
                      }
                      {nikoPatientDetail.primaryActive.insuranceId.current == nikoPatientDetail.primaryActive.insuranceId.initial && nikoPatientDetail.primaryActive.policyNo.current != nikoPatientDetail.primaryActive.policyNo.initial &&
                        <div className="u-flex--default u-flex--gap--2 c-message--content">
                          <p><b>Primary Active Policy Number Changed</b></p>
                          <label className="type--sml type--color--opaque"><b>{nikoPatientDetail.primaryActive.policyNo.initial}</b></label>
                          <p className="type--sml"><i className="u-icon u-icon--xxsml u-icon--arrow-text"></i></p>
                          <p className="type--sml"><b>{nikoPatientDetail.primaryActive.policyNo.current}</b></p>
                          {renderChangeHistory("PrimaryActivePolicyNo")}
                        </div>
                      }
                      {nikoPatientDetail.primaryInactive.insuranceId.current && nikoPatientDetail.primaryInactive.insuranceId.current != nikoPatientDetail.primaryInactive.insuranceId.initial &&
                        <div className="u-flex--default u-flex--gap--2 c-message--content">
                          <p className="type--sml"><b>Primary Inactive Changed</b></p>
                          <label className="type--sml type--color--opaque"><b>{nikoPatientDetail.primaryInactive.insurance.initial ? nikoPatientDetail.primaryInactive.insurance.initial.Name : "Empty"}</b></label>
                          <p className="type--sml"><i className="u-icon u-icon--xxsml u-icon--arrow-text"></i></p>
                          <p className="type--sml"><b>{nikoPatientDetail.primaryInactive.insurance.current.name}</b></p>
                          {renderChangeHistory("PrimaryInactiveInsuranceId")}
                        </div>
                      }
                      {nikoPatientDetail.primaryInactive.insuranceId.current && nikoPatientDetail.primaryInactive.insuranceId.current == nikoPatientDetail.primaryInactive.insuranceId.initial && nikoPatientDetail.primaryInactive.policyNo.current != nikoPatientDetail.primaryInactive.policyNo.initial &&
                        <div className="u-flex--default u-flex--gap--2 c-message--content">
                          <p className="type--sml"><b>Primary Inactive Policy Number Changed</b></p>
                          <label className="type--sml type--color--opaque"><b>{nikoPatientDetail.primaryInactive.policyNo.initial}</b></label>
                          <p className="type--sml"><i className="u-icon u-icon--xxsml u-icon--arrow-text"></i></p>
                          <p className="type--sml"><b>{nikoPatientDetail.primaryInactive.policyNo.current}</b></p>
                          {renderChangeHistory("PrimaryInactivePolicyNo")}
                        </div>
                      }
                      {nikoPatientDetail.secondaryActive.insuranceId.current != nikoPatientDetail.secondaryActive.insuranceId.initial &&
                        <div className="u-flex--default u-flex--gap--2 c-message--content">
                          <p className="type--sml"><b>Secondary Active Changed</b></p>
                          <label className="type--sml type--color--opaque"><b>{nikoPatientDetail.secondaryActive.insurance.initial ? nikoPatientDetail.secondaryActive.insurance.initial.Name : "Empty"}</b></label>
                          <p className="type--sml"><i className="u-icon u-icon--xxsml u-icon--arrow-text"></i></p>
                          <p className="type--sml"><b>{nikoPatientDetail.secondaryActive.insurance.current ? nikoPatientDetail.secondaryActive.insurance.current.name : "Empty"}</b></p>
                          {renderChangeHistory("SecondaryActiveInsuranceId")}
                        </div>
                      }
                      {nikoPatientDetail.secondaryActive.insuranceId.current == nikoPatientDetail.secondaryActive.insuranceId.initial && nikoPatientDetail.secondaryActive.policyNo.current != nikoPatientDetail.secondaryActive.policyNo.initial &&
                        <div className="u-flex--default u-flex--gap--2 c-message--content">
                          <p className="type--sml"><b>Secondary Active Policy Number Changed</b></p>
                          <label className="type--sml type--color--opaque"><b>{nikoPatientDetail.secondaryActive.policyNo.initial}</b></label>
                          <p className="type--sml"><i className="u-icon u-icon--xxsml u-icon--arrow-text"></i></p>
                          <p className="type--sml"><b>{nikoPatientDetail.secondaryActive.policyNo.current}</b></p>
                          {renderChangeHistory("SecondaryActivePolicyNo")}
                        </div>
                      }
                      {nikoPatientDetail.secondaryInactive.insuranceId.current && nikoPatientDetail.secondaryInactive.insuranceId.current != nikoPatientDetail.secondaryInactive.insuranceId.initial &&
                        <div className="u-flex--default u-flex--gap--2 c-message--content">
                          <p className="type--sml"><b>Secondary Inactive Changed</b></p>
                          <label className="type--sml type--color--opaque"><b>{nikoPatientDetail.secondaryInactive.insurance.initial ? nikoPatientDetail.secondaryInactive.insurance.initial.Name : "Empty"}</b></label>
                          <p className="type--sml"><i className="u-icon u-icon--xxsml u-icon--arrow-text"></i></p>
                          <p className="type--sml"><b>{nikoPatientDetail.secondaryInactive.insurance.current.name}</b></p>
                          {renderChangeHistory("SecondaryInactiveInsuranceId")}
                        </div>
                      }
                      {nikoPatientDetail.secondaryInactive.insuranceId.current && nikoPatientDetail.secondaryInactive.insuranceId.current == nikoPatientDetail.secondaryInactive.insuranceId.initial && nikoPatientDetail.secondaryInactive.policyNo.current != nikoPatientDetail.secondaryInactive.policyNo.initial &&
                        <div className="u-flex--default u-flex--gap--2 c-message--content">
                          <p className="type--sml"><b>Secondary Inactive Policy Number Changed</b></p>
                          <label className="type--sml type--color--opaque"><b>{nikoPatientDetail.secondaryInactive.policyNo.initial}</b></label>
                          <p className="type--sml"><i className="u-icon u-icon--xxsml u-icon--arrow-text"></i></p>
                          <p className="type--sml"><b>{nikoPatientDetail.secondaryInactive.policyNo.current}</b></p>
                          {renderChangeHistory("SecondaryInactivePolicyNo")}
                        </div>
                      }
                      {nikoPatientDetail.eligibilityStatusId != (nikoPatientDetail.initialNikoPatientDetail ? nikoPatientDetail.initialNikoPatientDetail.EligibilityStatusId : null) &&
                        <div className="u-flex--default u-flex--gap--2 c-message--content">
                          <p className="type--sml"><b>Insurance status changed</b></p>
                          <label className="type--sml type--color--opaque"><b>{nikoPatientDetail.initialNikoPatientDetail && nikoPatientDetail.initialNikoPatientDetail.EligibilityStatus ? nikoPatientDetail.initialNikoPatientDetail.EligibilityStatus.Name : "Empty"}</b></label>
                          <p className="type--sml"><i className="u-icon u-icon--xxsml u-icon--arrow-text"></i></p>
                          <p className="type--sml"><b>{nikoPatientDetail.eligibilityStatus ? nikoPatientDetail.eligibilityStatus.name : "Empty"}</b></p>
                          {renderChangeHistory("EligibilityStatusId")}
                        </div>
                      }
                      {nikoPatientDetail.patientTemplates.map(pt => {
                        return (!pt.isNew && (pt.isPriorAuthNeeded ? true : false) != (pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded ? true : false) &&
                          <div key={pt.id + "-par"} className="u-flex--default u-flex--gap--2 c-message--content">
                            <p className="type--sml"><b>{`${pt.subCategory.name} template ${pt.portalId} PAR changed`}</b></p>
                            <label className="type--sml type--color--opaque"><b>{pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded ? "TRUE" : "FALSE"}</b></label>
                            <p className="type--sml"><i className="u-icon u-icon--xxsml u-icon--arrow-text"></i></p>
                            <p className="type--sml"><b>{pt.isPriorAuthNeeded ? "TRUE" : "FALSE"}</b></p>
                            {renderChangeHistory("PAR_" + pt.id)}
                          </div>
                        );
                      })
                      }
                    </React.Fragment>
                  }
                </div>
              }
            </div>
          </div>
        </React.Fragment>

        <div className="card--tertiary__body" style={{ cursor: "pointer", paddingBottom: nikoPatientDetail.templatesVisible ? '0' : undefined }} onClick={() => changeItemsVisibility(nikoPatientDetail)}>
          <div className="u-flex u-flex--default">
            <span className="type--color--opaque">{nikoPatientDetail.templatesVisible ? 'Hide Orders' : 'See Orders'}</span>
            <i className={`u-icon u-icon--xsml ${nikoPatientDetail.templatesVisible ? 'u-icon--arrow-up' : 'u-icon--arrow-down'} u-mar--left--sml`} />
          </div>
        </div>
        {nikoPatientDetail.templatesVisible &&
          <div className="card--tertiary__body is-niko">
            {
              nikoPatientDetail.patientTemplates.map(patientTemplate => {
                return (
                  <PatientTemplateTable
                    key={patientTemplate.id}
                    nikoFacilityOrderInsuranceReviewPreviewViewStore={nikoFacilityOrderInsuranceReviewPreviewViewStore}
                    patientTemplate={patientTemplate}
                    nikoPatientDetail={nikoPatientDetail}
                  />
                );
              })
            }
          </div>
        }
      </div>
    </React.Fragment>
  );
});

NikoPatientTemplate.propTypes = {
  nikoFacilityOrderInsuranceReviewPreviewViewStore: PropTypes.object.isRequired,
  nikoPatientDetail: PropTypes.object.isRequired
};

const PatientTemplateTable = observer(({ nikoFacilityOrderInsuranceReviewPreviewViewStore, patientTemplate, nikoPatientDetail }) => {
  const {
    disabledByStatus,
    disabledByCompletion,
    disabledBySendOrder,
    disabled,
    selectedStatusAbrv,
    inProgressStatusId,
    changeStatusId,
    noChangeStatusId,
    dischargeStatusId,
  } = nikoFacilityOrderInsuranceReviewPreviewViewStore;

  return (
    <React.Fragment>
      {selectedStatusAbrv !== "discharge" && !((!patientTemplate.isNew) && patientTemplate.intakeStatusId === inProgressStatusId) &&
        <div className='niko__status__wrapper '>
          {patientTemplate.isNew ?
            <div className={`u-mar--right--status-top  ${disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled ? "is-disabled" : ""}`}>
              <div className="niko__status no-bg yellow">
                <div className="niko__status__holder">
                  <Button className="niko__status__btn" icon="u-icon u-icon--xsml u-icon--box--yellow" onClick={() => { }} disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}>NEW ORDER</Button>
                </div>
              </div>
            </div>
            :
            <div className={` u-mar--right--status-top ${disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled ? "is-disabled" : ""}`}>
              <div className={`niko__status ${patientTemplate.intakeStatusId === noChangeStatusId ? "success" : patientTemplate.intakeStatusId === changeStatusId ? "warning" : patientTemplate.intakeStatusId === dischargeStatusId ? "danger" : ""}`}>
                <div className="niko__status__holder ">
                  <Button
                    className={`niko__status__btn ${patientTemplate.intakeStatusId === noChangeStatusId ? "success" : patientTemplate.intakeStatusId === changeStatusId ? "warning" : patientTemplate.intakeStatusId === dischargeStatusId ? "danger" : ""}`}
                    onClick={() => { }}
                    disabled={disabledByCompletion || disabledByStatus || disabledBySendOrder || disabled || nikoPatientDetail.disabled}
                  >
                    {`${patientTemplate.intakeStatusId === noChangeStatusId ? "NO CHANGES" : patientTemplate.intakeStatusId === changeStatusId ? "CHANGES" : patientTemplate.intakeStatusId === dischargeStatusId ? "DISCONTINUED" : ""}`}
                  </Button>
                </div>
              </div>
            </div>
          }
        </div>
      }
      <div className={`table--secondary__wrapper no-hover-line u-mar--bottom--med--condition is-niko ${selectedStatusAbrv === "discharge" ? "danger" : patientTemplate.isNew ? "new" : patientTemplate.intakeSelectedStatus}`}>
        <table className="table--secondary w--100">
          <colgroup>
            <col width={450} />
            <col width={180} />
            <col width={100} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th colSpan={4} className="btn--group__wrapper">
                <div className="u-flex--default">
                  <label>{patientTemplate.subCategory.name.toUpperCase()}</label>
                  {patientTemplate.isSystemDns &&
                    <div>
                      <div className="u-mar--left--xsml btn--disc__wrapper is-fr">
                        Do not supply
                      </div>
                    </div>
                  }
                  {patientTemplate.isUserDns &&
                    <div>
                      <div className="u-mar--left--xsml btn--disc__wrapper is-fr">
                        Do not supply - FR
                      </div>
                    </div>
                  }
                  {patientTemplate.isDiscontinued &&
                    <div>
                      <div className="u-mar--left--xsml btn--disc__wrapper">
                        Discontinued
                      </div>
                    </div>
                  }
                </div>
                {patientTemplate.note &&
                  <div className={`u-mar--top--sml u-mar--bottom--xsml c-message has-input u-flex--default w--100 c-message--note${patientTemplate.intakeStatusId === dischargeStatusId ? "--discontinued" : patientTemplate.intakeStatusId === noChangeStatusId ? "--green" : patientTemplate.intakeStatusId === changeStatusId ? "--orange" : patientTemplate.isNew ? "--primary" : ""} u-mar--top--tny`}>
                    <i className="u-icon u-icon--sml u-icon--danger-triangle" />
                    <input
                      className="input input--sml input--message u-flex--grow--1 type--sml"
                      value={patientTemplate.note}
                      disabled
                      readOnly
                    />
                  </div>
                }
              </th>
            </tr>
          </thead>
        </table>
        <table className="table--secondary w--100">
          <colgroup>
            <col width={450} />
            <col width={180} />
            <col width={100} />
            <col width={100} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th className="table__label--sml"></th>
              <th className="table__label--sml"></th>
              <th className="table__label--sml"></th>
              <th className="table__label--sml"></th>
              <th className="table__label--sml"></th>
            </tr>
          </thead>
          <tbody style={(patientTemplate.isSystemDns || patientTemplate.isUserDns) ? { opacity: "0.5" } : {}}>
            {patientTemplate.patientTemplateItems.map((item) => {
              return (
                <tr key={item.id}>
                  <td className="table-col-input is-flex">
                    {item.product && <React.Fragment>{item.product.description} {(item.isBackordered != null ? item.isBackordered : item.product.backordered) && <i className="u-icon u-icon--med-lrg u-icon--backordered"></i>}</React.Fragment>}
                  </td>
                  <td>
                    {item.product && item.product.itemId}
                  </td>
                  <td>
                    {item.product && item.product.procedureCode}
                  </td>
                  <td className="table-col-input text-align-left">
                    {item.product && item.quantity}
                  </td>
                  <td className="table-col-input">
                    {item.note}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </React.Fragment>
  );
});

PatientTemplateTable.propTypes = {
  nikoFacilityOrderInsuranceReviewPreviewViewStore: PropTypes.object.isRequired,
  patientTemplate: PropTypes.object.isRequired,
  nikoPatientDetail: PropTypes.object.isRequired
};

export default defaultTemplate(NikoFacilityOrderInsuranceReviewPreviewTemplate);
